import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { ThemeProvider } from 'styled-components';
import {
	isEmailVerifiedSignalEnabled,
	isCustomPagesUiEnabled,
	isHubsEnabledInHelpCenter,
} from '@helpcenter/feature-flags';
import { lightenColor, readableTextColor } from '@helpcenter/styles/src/colors';
import { GlobalStyled } from '@helpcenter/styles/src/global';
import { createMemoryHistory } from 'history';
import type { BrowserHistory, Route } from 'react-resource-router';
import { RouteComponent, Router } from 'react-resource-router';
import { getViewHomePageRoute, getEditHomePageRoute } from 'routes/advanced-help-center';
import { aiAnswerRoute } from 'routes/aianswer';
import { approvalsRoute } from 'routes/approvals';
import { consentFormRoute } from 'routes/consent-form';
import { csmContactUsRoute } from 'routes/csm-contact-us';
import { getViewCustomPageRoute } from 'routes/csm-custom-page';
import { csmFolderRoute } from 'routes/csm-folder';
import {
	csmViewArticleRoute,
	csmViewCrossSiteArticleRoute,
	csmViewKbPageRoute,
} from 'routes/csm-kb';
import { csmRequestsListRoute, csmRequestDetailsRoute } from 'routes/csm-request';
import { csmUserProfileRoute } from 'routes/csm-user-profile';
import { getEditCustomPageRoute } from 'routes/custom-pages';
import { addTopicRoute, editTopicRoute } from 'routes/edit-topic';
import {
	embedKBArticleRoute,
	embedSinglePortalRoute,
	getEmbedCreateRequestRoute,
} from 'routes/embed-smartlinks';
import { getHelpCenterRoute } from 'routes/help-center';
import {
	viewAllSearchResultsRoute,
	viewPortalAllSearchResultsRoute,
	viewPortalArticleRoute,
	viewCrossSitePortalArticleRoute,
	viewPortalCategoryArticleRoute,
	viewPortalKnowledgeCategoryRoute,
} from 'routes/kb';
import { loginRoute, portalLoginRoute } from 'routes/login';
import { portalAccessForbiddenRoute } from 'routes/portal-access-forbidden';
import { getPortalHomeRoutes } from 'routes/portal-home';
import { requestApprovalRoute } from 'routes/request-approval';
import { requestDetailsRoute } from 'routes/request-details';

import {
	portalCompleteSignupRoute,
	portalInvitedSignupRoute,
	portalResetPasswordRoute,
	portalSignupRoute,
	userCheckMailRoute,
	userCompleteSignupRoute,
	userResetPasswordRoute,
	userSignupRoute,
} from 'routes/signup';
import { getViewTopicRoute } from 'routes/topics';
import { userVerifyEmailRoute } from 'routes/verify-email';
import type { State } from 'state';
import { getHeadless } from 'state/selectors/env';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { getUserState } from 'state/selectors/user';
import { UFOWatcher } from 'utils/ufo-watcher';
import {
	getCsmUserProfileRouteWithHubShell,
	getCsmRequestsListRouteWithHubShell,
	getCsmContactUsRouteWithHubShell,
	getHubHomepageRoute,
	getNotFoundRouteWithHubShell,
	getCsmFolderRouteWithHubShell,
	getCsmRequestDetailsRouteWithHubShell,
	getCsmViewPortalArticleRouteWithHubShell,
	getPortalAccessForbiddenRouteWithHubShell,
	getCsmViewArticleRouteWithHubShell,
	getCsmViewCrossSiteArticleRouteWithHubShell,
	getCsmViewCrossSitePortalArticleRouteWithHubShell,
	getHubCustompageRoute,
	getLoginRouteWithHubShell,
	getSignupRouteWithHubShell,
	getCompleteSignupRouteWithHubShell,
	getResetPasswordRouteWithHubShell,
	getCheckEmailRouteWithHubShell,
} from 'view/hub-routes';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	isFlexiHCEnabledInDefaultHC,
	isAdvancedHelpCenter,
	isJcsOnlyRestrictedRoute,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv, getIsHCCustomizationEnabled } from '@atlassian/help-center-common-util/env';
import { getBaseName, history } from '@atlassian/help-center-common-util/history';

import { routeWithAiWidgetConfigResource } from '../../route-with-widget-config-resource/route-with-widget-config-resource';
import JsmResourceRouter from '../jsm-resource-router';
import { resourcesPlugin } from '../resource-plugin';
import {
	anonymousRequestSuccessRoute,
	notFoundErrorRoute,
	portalReorderRoute,
	getRequestCreateRoute,
	requestFeedbackRoute,
	unsubscribeRoute,
	helpCenterRequestFeedbackRoute,
} from '../routes';

// Check if enhanced article view feature gate is enabled
const isEnhancedArticleViewEnabled = (): boolean => {
	return FeatureGates.checkGate('jcs_enhanced_article_view');
};

// Helper function to determine which requests list route to use
const getRequestsListRoute = () => {
	const isHubsEnabled = isHubsEnabledInHelpCenter();

	if (isHubsEnabled) {
		return getCsmRequestsListRouteWithHubShell();
	}

	return csmRequestsListRoute;
};

const getCustomPageRoutes = (): Route[] => {
	return isCustomPagesUiEnabled() && getIsHCCustomizationEnabled()
		? [
				getEditCustomPageRoute(),
				isHubsEnabledInHelpCenter()
					? getHubCustompageRoute()
					: routeWithAiWidgetConfigResource(getViewCustomPageRoute()),
			]
		: [];
};

const getAnonymousRoutes = (): Route[] => {
	return [
		isHubsEnabledInHelpCenter() ? getLoginRouteWithHubShell() : loginRoute,
		isHubsEnabledInHelpCenter() ? getResetPasswordRouteWithHubShell() : userResetPasswordRoute,
		isHubsEnabledInHelpCenter() ? getSignupRouteWithHubShell() : userSignupRoute,
		isHubsEnabledInHelpCenter() ? getCompleteSignupRouteWithHubShell() : userCompleteSignupRoute,
		isHubsEnabledInHelpCenter() ? getCheckEmailRouteWithHubShell() : userCheckMailRoute,
	];
};

const getActiveCsmRoutes = () => {
	return {
		activeViewArticleRoute: csmViewArticleRoute,
		activeKbPageRoute: csmViewKbPageRoute,
		activeCrossSiteArticleRoute: csmViewCrossSiteArticleRoute,
		activeUserProfileRoute: csmUserProfileRoute,
	};
};

const getAccessAndAuthenticationRoutes = () => {
	return [
		isHubsEnabledInHelpCenter()
			? getPortalAccessForbiddenRouteWithHubShell()
			: portalAccessForbiddenRoute,
		portalLoginRoute,
		portalSignupRoute,
		portalResetPasswordRoute,
		portalCompleteSignupRoute,
		portalInvitedSignupRoute,
	];
};

const getUserProfileRoute = () => {
	const { activeUserProfileRoute } = getActiveCsmRoutes();

	return routeWithAiWidgetConfigResource(
		isHubsEnabledInHelpCenter() ? getCsmUserProfileRouteWithHubShell() : activeUserProfileRoute,
	);
};

const getSearchAndArticleRoutes = () => {
	const { activeViewArticleRoute, activeKbPageRoute, activeCrossSiteArticleRoute } =
		getActiveCsmRoutes();
	return [
		routeWithAiWidgetConfigResource(activeKbPageRoute),
		viewAllSearchResultsRoute,
		viewPortalAllSearchResultsRoute,
		viewPortalCategoryArticleRoute,
		isHubsEnabledInHelpCenter()
			? getCsmViewPortalArticleRouteWithHubShell()
			: viewPortalArticleRoute,
		isHubsEnabledInHelpCenter()
			? getCsmViewCrossSitePortalArticleRouteWithHubShell()
			: viewCrossSitePortalArticleRoute,
		routeWithAiWidgetConfigResource(
			isHubsEnabledInHelpCenter() ? getCsmViewArticleRouteWithHubShell() : activeViewArticleRoute,
		),
		isHubsEnabledInHelpCenter()
			? getCsmViewCrossSiteArticleRouteWithHubShell()
			: activeCrossSiteArticleRoute,
	];
};

const getContactUsRoute = () => {
	return routeWithAiWidgetConfigResource(
		isHubsEnabledInHelpCenter() ? getCsmContactUsRouteWithHubShell() : csmContactUsRoute,
	);
};

export const getRoutesOld = (): Route[] => {
	const isAdvancedOrFlexiHCEnabled = isAdvancedHelpCenter() || isFlexiHCEnabledInDefaultHC();

	return [
		...(isHubsEnabledInHelpCenter() ? [getHubHomepageRoute()] : []),
		...(isAdvancedOrFlexiHCEnabled
			? [routeWithAiWidgetConfigResource(getViewHomePageRoute())]
			: []),
		...(isAdvancedOrFlexiHCEnabled ? [getEditHomePageRoute()] : []),
		...(isEmailVerifiedSignalEnabled() ? [userVerifyEmailRoute] : []),
		aiAnswerRoute,
		consentFormRoute,
		...getAnonymousRoutes(),
		...getAccessAndAuthenticationRoutes(),
		requestFeedbackRoute,
		unsubscribeRoute,
		getHelpCenterRoute(),
		requestDetailsRoute,
		...getSearchAndArticleRoutes(),
		routeWithAiWidgetConfigResource(getRequestsListRoute()),
		...[
			isHubsEnabledInHelpCenter()
				? getCsmRequestDetailsRouteWithHubShell()
				: csmRequestDetailsRoute,
		],
		helpCenterRequestFeedbackRoute,
		approvalsRoute,
		requestApprovalRoute,
		getUserProfileRoute(),
		viewPortalKnowledgeCategoryRoute,
		anonymousRequestSuccessRoute,
		getRequestCreateRoute(),
		getPortalHomeRoutes(),
		getEmbedCreateRequestRoute(),
		embedSinglePortalRoute,
		embedKBArticleRoute,
		portalReorderRoute(true),
		editTopicRoute,
		addTopicRoute,
		getViewTopicRoute(),
		getContactUsRoute(),
		...(isEnhancedArticleViewEnabled()
			? [
					routeWithAiWidgetConfigResource(
						isHubsEnabledInHelpCenter() ? getCsmFolderRouteWithHubShell() : csmFolderRoute,
					),
				]
			: []),
		...getCustomPageRoutes(),
		isHubsEnabledInHelpCenter() ? getNotFoundRouteWithHubShell() : notFoundErrorRoute,
	];
};

export interface Props {
	headerless: boolean;
	themeColor: string;
	titleColor: string;
	user?: {
		externalCustomer: boolean;
	};
}

export const CSMResourceRouterOld = (props: Props) => {
	const { themeColor, titleColor } = props;
	const readableTextColorFromTheme = readableTextColor(themeColor);
	const lightColorFromTheme = lightenColor(themeColor);

	const routes = useMemo(() => {
		return getRoutesOld();
	}, []);
	// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-react-fc
	const location = window.location.pathname + window.location.search;

	const theme = {
		titleColor,
		readableTextColor: readableTextColorFromTheme,
		color: themeColor,
		lightColor: lightColorFromTheme,
	};

	if (__SERVER__) {
		return (
			<ThemeProvider theme={theme}>
				<Router
					routes={routes}
					isGlobal={false}
					basePath={getBaseName()}
					history={createMemoryHistory({ initialEntries: [location] })}
					plugins={[resourcesPlugin]}
				>
					<GlobalStyled isCsm />
					<RouteComponent />
				</Router>
			</ThemeProvider>
		);
	}

	//isGlobal=false, to avoid the router conflicting with the sidebar's router.
	return (
		<ThemeProvider theme={theme}>
			<Router
				routes={routes}
				history={history as BrowserHistory}
				isGlobal={false}
				plugins={[resourcesPlugin]}
			>
				<GlobalStyled isCsm />
				{!__SERVER__ ? <UFOWatcher /> : null}
				<RouteComponent />
			</Router>
		</ThemeProvider>
	);
};

const selectorOld = createSelector(
	getHeadless,
	getThemeColor,
	getHelpCenterTitleColor,
	(headerless, themeColor, titleColor) => ({
		headerless,
		themeColor,
		titleColor,
	}),
);

export const getRoutes = ({
	externalCustomer,
}: {
	externalCustomer: boolean | undefined;
}): Route[] => {
	// Routes that are restricted to non external users when
	// base path is /servicedesk/customer and customerAccountAvailabilty is JCS_ONLY
	const isAdvancedOrFlexiHCEnabled = isAdvancedHelpCenter() || isFlexiHCEnabledInDefaultHC();

	const restrictedRoutes = [
		...(isHubsEnabledInHelpCenter() ? [getHubHomepageRoute()] : []),
		...(isAdvancedOrFlexiHCEnabled
			? [routeWithAiWidgetConfigResource(getViewHomePageRoute())]
			: []),
		...(isAdvancedOrFlexiHCEnabled ? [getEditHomePageRoute()] : []),
		...(isEmailVerifiedSignalEnabled() ? [userVerifyEmailRoute] : []),
		aiAnswerRoute,
		consentFormRoute,
		...getAnonymousRoutes(),
		...getAccessAndAuthenticationRoutes(),
		requestFeedbackRoute,
		unsubscribeRoute,
		getHelpCenterRoute(),
		requestDetailsRoute,
		...getSearchAndArticleRoutes(),
		routeWithAiWidgetConfigResource(getRequestsListRoute()),
		...[
			isHubsEnabledInHelpCenter()
				? getCsmRequestDetailsRouteWithHubShell()
				: csmRequestDetailsRoute,
		],
		helpCenterRequestFeedbackRoute,
		approvalsRoute,
		requestApprovalRoute,
		getUserProfileRoute(),
		getEmbedCreateRequestRoute(),
		embedSinglePortalRoute,
		embedKBArticleRoute,
		getContactUsRoute(),
		...(isEnhancedArticleViewEnabled()
			? [
					routeWithAiWidgetConfigResource(
						isHubsEnabledInHelpCenter() ? getCsmFolderRouteWithHubShell() : csmFolderRoute,
					),
				]
			: []),
		...getCustomPageRoutes(),
	];

	return [
		...(isJcsOnlyRestrictedRoute(getEnv().customerAccountAvailabilty) && externalCustomer
			? []
			: restrictedRoutes),
		isHubsEnabledInHelpCenter() ? getNotFoundRouteWithHubShell() : notFoundErrorRoute,
	];
};

export const CSMResourceRouter = (props: Props) => {
	const { themeColor, titleColor, user } = props;
	const readableTextColorFromTheme = readableTextColor(themeColor);
	const lightColorFromTheme = lightenColor(themeColor);
	const routes = useMemo(() => {
		return getRoutes({ externalCustomer: user?.externalCustomer });
	}, [user]);
	// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-react-fc
	const location = window.location.pathname + window.location.search;

	const theme = {
		titleColor,
		readableTextColor: readableTextColorFromTheme,
		color: themeColor,
		lightColor: lightColorFromTheme,
	};

	// If JCS_ONLY and accessing a JSM route (path starts with /servicedesk/customer) and the user is not external, redirect to JSM
	if (isJcsOnlyRestrictedRoute(getEnv().customerAccountAvailabilty) && !user?.externalCustomer) {
		return <JsmResourceRouter />;
	}

	if (__SERVER__) {
		return (
			<ThemeProvider theme={theme}>
				<Router
					routes={routes}
					isGlobal={false}
					basePath={getBaseName()}
					history={createMemoryHistory({ initialEntries: [location] })}
					plugins={[resourcesPlugin]}
				>
					<GlobalStyled isCsm />
					<RouteComponent />
				</Router>
			</ThemeProvider>
		);
	}

	//isGlobal=false, to avoid the router conflicting with the sidebar's router.
	return (
		<ThemeProvider theme={theme}>
			<Router
				routes={routes}
				history={history as BrowserHistory}
				isGlobal={false}
				plugins={[resourcesPlugin]}
			>
				<GlobalStyled isCsm />
				{!__SERVER__ ? <UFOWatcher /> : null}
				<RouteComponent />
			</Router>
		</ThemeProvider>
	);
};

const selector = createSelector(
	getHeadless,
	getThemeColor,
	getHelpCenterTitleColor,
	(state: State) => getUserState(state),
	(headerless, themeColor, titleColor, userState) => ({
		headerless,
		themeColor,
		titleColor,
		user: {
			externalCustomer: userState.externalCustomer,
		},
	}),
);

export const CSMResourceRouterNew = connect(selector)(CSMResourceRouter);

export default connect(selectorOld)(CSMResourceRouterOld);
